import { actionCreators } from 'app/area/dashboard/actions';
import {
  selectContainerLayout, selectExecutionCounter, selectFetchingMainFavourite,
  selectMainFavouriteId, selectSelectionReportInstances, selectCaseTableViewInstances, selectRiskReportInstances,
  selectBowtieReportInstances
} from 'app/area/dashboard/selectors';
import { selectCaseListingInstances } from 'app/area/case-report/case-listing/selectors';
import { DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FavouriteDefinition } from 'app/types-shared';
import { api } from './api';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { LocationWithQuery } from 'app/components/enhancers/with-router';
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { EXECUTE_STATUS } from 'app/area/reports/containers/chart/types-store';
import { selectFavouriteInstances } from 'app/area/favourite/selectors';

export function useSelectors(dashboardType: number) {
  const containerLayout = useSelector(selectContainerLayout(dashboardType));
  const fetchingMainFavourite = useSelector(selectFetchingMainFavourite);
  const mainFavouriteId = useSelector(selectMainFavouriteId);

  return {
    containerLayout,
    fetchingMainFavourite,
    mainFavouriteId
  };
}

export function useActionCreators() {
  const dispatch = useDispatch();
  return useMemo(() => bindActionCreators(actionCreators, dispatch), [dispatch]);
}

export function useDrillDown(mainFavouriteId: number) {
  const {
    drillDownToCaseListing
  } = useActionCreators();

  return useCallback((favouriteId: number, model: DrillDownModel) => {
    drillDownToCaseListing(mainFavouriteId, favouriteId, model);
  }, [mainFavouriteId]);
}

export function useCreateMainFavouriteEffect(
  dashboardType: number,
  requestedMainFavouriteId: number | undefined,
  mainFavouriteReadOnly: boolean,
  location: LocationWithQuery
) {
  const {
    createMainFavourite,
    clearMainFavourite
  } = useActionCreators();

  useEffect(() => {
    createMainFavourite(dashboardType, requestedMainFavouriteId ?? 0, mainFavouriteReadOnly, location);
  }, [dashboardType, requestedMainFavouriteId, mainFavouriteReadOnly, location]);

  useEffect(() => () => { clearMainFavourite() }, []);
}

export function useLoadContainerDefinitionsEffect(dashboardType: number, layoutTypeContextId: number) {
  const {
    loadContainerDefinitions
  } = useActionCreators();

  useEffect(() => {
    loadContainerDefinitions(dashboardType, layoutTypeContextId);
  }, [dashboardType, layoutTypeContextId]);
}

export function useDashboardExecuteEffect(definition: FavouriteDefinition) {
  const fetchingMainFavourite = useSelector(selectFetchingMainFavourite);
  const mainFavouriteId = useSelector(selectMainFavouriteId);

  const {
    executeDashboard
  } = useActionCreators();

  useEffect(() => {
    if (!fetchingMainFavourite && mainFavouriteId && definition) {
      executeDashboard();
    }
  }, [fetchingMainFavourite, mainFavouriteId, definition]);

  return useSelector(selectExecutionCounter);
}

export function useReportApiOverrides(mainFavouriteId: number): Partial<ReportCommonApi> {
  return useMemo(() => ({
    executeFavourite: (definition: FavouriteDefinition,
      startIndex?: number, maxResults?: number, ovverideApi?: boolean) =>
      api.executeFavourite(definition, mainFavouriteId, startIndex, maxResults, ovverideApi),
    drilldown: (definition: FavouriteDefinition, model: DrillDownModel) =>
      definition?.favourite?.v
        ? api.drillDown(mainFavouriteId, definition.favourite.v, model)
        : Promise.reject(new Error('Favourite id is required for dashboard drilldown'))
  }), [mainFavouriteId]);
}

export function useIsDashboardDisabled(definition?: any) {
  const selectedDashboardDefinition = definition?.reports?.filter(report => report.report.v === definition.selectedReport.v)[0];
  const disabledDate = new Date(selectedDashboardDefinition?.dashboard?.disabled);
  return disabledDate?.getTime() <= new Date().getTime();
}

export function useChartsLoadingComplete() {
  const chartInstances = useSelector(selectSelectionReportInstances);
  const caseListingInstances = useSelector(selectCaseListingInstances);
  const caseTableViewInstances = useSelector(selectCaseTableViewInstances);
  const riskReportInstances = useSelector(selectRiskReportInstances);
  const dashboardInstanceKey = useInstanceKey();
  const bowtieReportInstances = useSelector(selectBowtieReportInstances);
  const favouriteInstances = useSelector(selectFavouriteInstances);
  const favouriteInstancesWithoutMainFavourite = Object.keys(favouriteInstances ?? {})
    .filter(key => key !== dashboardInstanceKey);
  
  
  // Return false if no instances exist yet (nothing to wait for)
  if (favouriteInstancesWithoutMainFavourite.length === 0) {
    return false;
  }
  
  // Check that all instances have succeeded in at least one slice
  const allInstancesComplete = favouriteInstancesWithoutMainFavourite.every(key => {
    // Bowtie favourites register an instance in the bowtie slice as soon as the bowtie
    // listing mounts. When present, it is the source of truth for that favourite, because
    // the caseListing slice only reflects that the list of cases loaded - not that the
    // bowtie canvas finished. Other report types never create a bowtie instance.
    const bowtieInstance = bowtieReportInstances?.[key];
    if (bowtieInstance) {
      return bowtieInstance.executeStatus === EXECUTE_STATUS.SUCCEEDED ||
        bowtieInstance.executeStatus === EXECUTE_STATUS.FAILED;
    }

    // For non-bowtie reports, check the remaining slices.
    const statuses = [
      chartInstances?.[key]?.chartState?.executeStatus,
      caseListingInstances?.[key]?.executeStatus,
      caseTableViewInstances?.[key]?.executeStatus,
      riskReportInstances?.[key]?.executeStatus
    ];

    // Each favourite executes in only one slice; others remain IDLE. Check if the active slice reached a terminal state.
    return statuses.some(status => 
      status === EXECUTE_STATUS.SUCCEEDED || status === EXECUTE_STATUS.FAILED
    );
  });

  return allInstancesComplete;
}
